import * as React from 'react';
import { TooltipHost } from '@fluentui/react';
import { SubscriptionStatusEnum, SubscriptionTierEnum } from '../data-structures/enums';
import { LanguageContext } from '../utilities/LocalizationModule';
import { Pill, Tone } from './Pill';

interface Props {
    // Stringified status as returned by the API (capitalized: "Active", "Canceled", "Suspended", "Pending", "Completed", or "pending"/lowercase variants).
    status: string;
    // Subscription tier ("Trial" / "Paid"). An active Trial renders a dedicated "Free trial ends DATE" pill instead of a plain "Active" badge.
    tier?: string;
    // Local-side scheduled cancellation timestamp (Stripe). When set in the future, the pill flips to "Cancels DATE" regardless of status.
    cancelAtRaw?: Date | null;
    // Period-end timestamp. Used as the "cancels on" date for non-Stripe rows whose Status is already Canceled but expiry is in the future.
    expiryDateRaw?: Date | null;
    // Stripe-only: latest invoice id while that invoice is open and unpaid. On an Active row this
    // renders "Awaiting payment · due DATE" (send_invoice / bank transfer). Ignored on non-Active rows.
    openInvoiceId?: string | null;
    // Due date of that open invoice; null for charge_automatically invoices (no due date) → plain label.
    openInvoiceDueAtRaw?: Date | null;
}

// "21 Jun" when the date is in the current year, "25 May 2027" otherwise. The month abbreviation follows
// the browser locale (undefined), like every other date in the grid (toLocaleDateString in ApiHelper), so
// a Dutch or German viewer sees "30 sep" / "30. Sept." instead of a hard-coded English month. Exported so
// the Payments panel's open-invoice bar formats the due date exactly like the pill.
export function formatShortDate(date: Date): string {
    const now = new Date();
    const sameYear = date.getFullYear() === now.getFullYear();
    const month = date.toLocaleString(undefined, { month: 'short' });
    return sameYear
        ? `${date.getDate()} ${month}`
        : `${date.getDate()} ${month} ${date.getFullYear()}`;
}
const formatCancelsOn = formatShortDate;

function resolveFutureCancelDate(status: string, cancelAtRaw?: Date | null, expiryDateRaw?: Date | null): Date | null {
    const now = new Date();
    const statusKey = (status || '').toLowerCase();

    // (A) Stripe future-cancellation: CancelAt is set, sub is still Active. We gate on
    //     Status === Active because a stale CancelAt can linger on rows that are already
    //     terminal: ProjectDeletedSubscriptionAsync and the missing-item-detection path
    //     don't clear CancelAt when they flip Status to Canceled, so without the guard
    //     an already-canceled row could falsely render "Cancels FUTURE DATE".
    if (statusKey === SubscriptionStatusEnum.Active && cancelAtRaw && cancelAtRaw > now) {
        return cancelAtRaw;
    }

    // (B) Non-Stripe / invoice flow: row flipped to Canceled with a future ExpiryDate.
    //     Existing portal logic already treats this as "still has access until expiry".
    if (statusKey === SubscriptionStatusEnum.Canceled && expiryDateRaw && expiryDateRaw > now) {
        return expiryDateRaw;
    }

    return null;
}

export type AwaitingPaymentKind = 'due' | 'overdue' | 'nodate';
export interface AwaitingPaymentState { kind: AwaitingPaymentKind; dueAt: Date | null; }

// Status-agnostic view of the row's open invoice (Subscription.OpenInvoiceId / OpenInvoiceDueAt): null when
// nothing is outstanding, otherwise whether the due date is ahead, passed, or absent (charge_automatically
// invoices carry no due_date). An invalid Date (NaN) is treated as "no due date" rather than rendering NaN.
// Shared by the pill (Active rows only, below) and the Payments panel's info bar (Active + Suspended).
export function resolveOpenInvoiceState(
    openInvoiceId?: string | null,
    openInvoiceDueAtRaw?: Date | null,
    now: Date = new Date()
): AwaitingPaymentState | null {
    if (!openInvoiceId) return null;
    if (!openInvoiceDueAtRaw || isNaN(openInvoiceDueAtRaw.getTime())) return { kind: 'nodate', dueAt: null };
    return openInvoiceDueAtRaw < now
        ? { kind: 'overdue', dueAt: openInvoiceDueAtRaw }
        : { kind: 'due', dueAt: openInvoiceDueAtRaw };
}

// "Awaiting payment" is an overlay on Status === Active only: Stripe keeps a send_invoice subscription
// active until the invoice's due date, and an async (SEPA/ACH) card-less payment leaves an open invoice
// on an active charge_automatically sub. Suspended/Canceled rows already say what matters, so the open
// invoice is ignored there.
export function resolveAwaitingPayment(
    status: string,
    openInvoiceId?: string | null,
    openInvoiceDueAtRaw?: Date | null,
    now: Date = new Date()
): AwaitingPaymentState | null {
    if ((status || '').toLowerCase() !== SubscriptionStatusEnum.Active) return null;
    return resolveOpenInvoiceState(openInvoiceId, openInvoiceDueAtRaw, now);
}

// Localized label for an open-invoice state: "Awaiting payment · due 30 Sep", "Payment overdue since
// 30 Sep" or plain "Awaiting payment". Templates come from LocalizationModule (en/nl/de) with English
// fallbacks; the date goes through formatShortDate. Shared by the pill and the Payments panel.
export function awaitingPaymentLabel(strings: any, state: AwaitingPaymentState): string {
    if (state.kind === 'nodate' || !state.dueAt) {
        return strings?.AwaitingPayment || 'Awaiting payment';
    }
    const template: string = state.kind === 'overdue'
        ? (strings?.PaymentOverdue || 'Payment overdue since {date}')
        : (strings?.AwaitingPaymentDue || 'Awaiting payment · due {date}');
    return template.replace('{date}', formatShortDate(state.dueAt));
}

const SubscriptionStatusPill: React.FC<Props> = ({ status, tier, cancelAtRaw, expiryDateRaw, openInvoiceId, openInvoiceDueAtRaw }) => {
    const languageStrings = React.useContext(LanguageContext);
    const futureCancel = resolveFutureCancelDate(status, cancelAtRaw, expiryDateRaw);

    if (futureCancel) {
        const cancelsTemplate: string = (languageStrings as any).CancelsOn || 'Cancels {date}';
        const label = cancelsTemplate.replace('{date}', formatCancelsOn(futureCancel));
        const tooltip = `${label} (${futureCancel.toLocaleString()})`;
        return (
            <TooltipHost content={tooltip}>
                <Pill tone="neutral" iconName="Clock" role="status" ariaLabel={tooltip}>
                    {label}
                </Pill>
            </TooltipHost>
        );
    }

    const statusKey = (status || '').toLowerCase();

    // Trial subscriptions: surface the trial end date as a dedicated pill instead of a
    // plain "Active" badge. Only an active trial with a *future* end date qualifies. Gating
    // on `> now` (mirroring resolveFutureCancelDate above) means: a cancelled/expired trial
    // falls through to the normal status rendering below; a stale row whose trial end has
    // already passed (backend lag) shows "Active" rather than a misleading past date; and an
    // unparseable expiry (Invalid Date) — for which the comparison is always false — also
    // falls through instead of rendering "NaN". Info tone and date formatting ("4 Jul" /
    // "4 Jul 2027") mirror the futureCancel pill.
    const isTrial = (tier || '').toLowerCase() === SubscriptionTierEnum.Trial.toLowerCase();
    if (isTrial && statusKey === SubscriptionStatusEnum.Active && expiryDateRaw && expiryDateRaw > new Date()) {
        const trialTemplate: string = (languageStrings as any).TrialEnds || 'Free trial ends {date}';
        const label = trialTemplate.replace('{date}', formatCancelsOn(expiryDateRaw));
        const tooltip = `${label} (${expiryDateRaw.toLocaleString()})`;
        return (
            <TooltipHost content={tooltip}>
                <Pill tone="info" role="status" ariaLabel={tooltip}>
                    {label}
                </Pill>
            </TooltipHost>
        );
    }

    // Awaiting payment (Stripe send_invoice / bank transfer): ranks below "Cancels DATE" (terminal,
    // rarer) and the trial pill (mutually exclusive anyway — Stripe rows are Tier=Paid), above the
    // plain status. Info tone while the due date is ahead; warning once it has passed (transient —
    // Stripe flips the sub to past_due → Suspended at that moment). Icon + tooltip carry the full
    // timestamp; the pill itself stays non-interactive (the Payments panel has the "View invoice" link).
    const awaiting = resolveAwaitingPayment(status, openInvoiceId, openInvoiceDueAtRaw);
    if (awaiting) {
        const label = awaitingPaymentLabel(languageStrings, awaiting);
        const tooltip = awaiting.dueAt ? `${label} (${awaiting.dueAt.toLocaleString()})` : label;
        return (
            <TooltipHost content={tooltip}>
                <Pill tone={awaiting.kind === 'overdue' ? 'warning' : 'info'} iconName="Money" role="status" ariaLabel={tooltip}>
                    {label}
                </Pill>
            </TooltipHost>
        );
    }

    let tone: Tone = 'neutral';
    let displayKey: string | null = null;
    switch (statusKey) {
        case SubscriptionStatusEnum.Active:
            tone = 'success';
            displayKey = 'Active';
            break;
        case SubscriptionStatusEnum.Suspended:
            tone = 'warning';
            // No localization key in LocalizationModule today; fall through to raw display.
            displayKey = null;
            break;
        case SubscriptionStatusEnum.Canceled:
            tone = 'danger';
            displayKey = 'Canceled';
            break;
        case SubscriptionStatusEnum.Pending:
            tone = 'info';
            displayKey = 'Pending';
            break;
        case SubscriptionStatusEnum.Completed:
        default:
            tone = 'neutral';
            displayKey = null;
            break;
    }

    // Prefer a localized string when we have one. Otherwise fall back to first-letter
    // capitalization of whatever the API returned so a previously-unhandled status
    // (e.g., a new Stripe value) is still rendered.
    const localized = displayKey ? (languageStrings as any)[displayKey] : null;
    const display = localized
        ? localized
        : status
            ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
            : '—';

    return (
        <Pill tone={tone} role="status" ariaLabel={display}>
            {display}
        </Pill>
    );
};

export default SubscriptionStatusPill;
